import React from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import RightworkSelect from "../../../../../components/shared/RightworkSelect";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { format, isValid } from "date-fns";
import EmployeeProfileSectionCard from "./EmployeeProfileSectionCard";
import EmployeePayForm from "./EmployeePayForm";
import { SOURCE_LABELS, payTypeOf } from "./PayClassificationField";
import Drawer from "@mui/material/Drawer";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import CloseIcon from "@mui/icons-material/Close";
import StorefrontIcon from "@mui/icons-material/Storefront";
import DrawerHeader from "../../shared/helpers/DrawerHeader";
import DrawerFooterContainerLarge from "../../shared/helpers/DrawerFooterContainerLarge";
import LoadingButton from "@mui/lab/LoadingButton";
import SaveIcon from "@mui/icons-material/Save";
import ButtonOutlined from "../../shared/helpers/ButtonOutlined";
import {
  API_get_pay_periods,
  API_get_store_team_member_wages,
  API_post_store_team_member_wage,
} from "../../../../../constants";
import { getRightworkAPI, postRightworkAPI } from "../../utilities/requests";
import { formatDateReadableWithDayOfWeek } from "../../utilities/helpers";

const INITIAL_FORM_STATE = {
  wageUuid: null,
  effectiveDate: "",
  storeTeamMemberRoleId: "",
  compensationType: "hourly",
  // Whether the classification is exempt (salary, or an existing hourly
  // exemption); sent with the wage as its classification.
  exempt: false,
  hourlyRate: "",
  salaryAmount: "",
  salaryInterval: "annual",
  notes: "",
};

const isSalaryType = (wageType) =>
  ["salary", "salaried"].includes(String(wageType || "").trim().toLowerCase());

// The drawer's pay classification <-> compensation type plus exempt flag.
const DRAWER_CLASSIFICATIONS = [
  { value: "hourly", label: "Hourly" },
  { value: "salary_non_exempt", label: "Salary (non-exempt)" },
  { value: "salary_exempt", label: "Salary (exempt)" },
];
const drawerClassificationOf = ({ compensationType, exempt }) =>
  compensationType === "hourly"
    ? exempt ? "hourly_exempt" : "hourly"
    : exempt ? "salary_exempt" : "salary_non_exempt";
// The wage's pending_exempt_status, applied when the wage takes effect.
// Hourly follows pay type unless it keeps an existing hourly exemption.
const pendingExemptStatusOf = ({ compensationType, exempt }) =>
  exempt ? "exempt" : compensationType === "hourly" ? "pay_type" : "non_exempt";
// "Hourly (exempt)" is only offered while it's the current state, so an
// existing hourly exemption is kept unless the admin changes it.
const drawerClassificationOptions = (value) =>
  value === "hourly_exempt"
    ? [DRAWER_CLASSIFICATIONS[0], { value: "hourly_exempt", label: "Hourly (exempt)" }, ...DRAWER_CLASSIFICATIONS.slice(1)]
    : DRAWER_CLASSIFICATIONS;

const salaryIntervalLabels = {
  annual: "per year",
  monthly: "per month",
  per_pay_period: "per pay period",
};

const salaryIntervalOptions = [
  { value: "annual", label: "Annual" },
  { value: "monthly", label: "Monthly" },
  { value: "per_pay_period", label: "Per pay period" },
];

const datePickerStyles = {
  "& .MuiOutlinedInput-root": {
    height: "45px",
    backgroundColor: "white",
    borderRadius: "8px",
    "& fieldset": {
      borderColor: "#e2e8f0",
    },
    "&:hover fieldset": {
      borderColor: "#6d28d9",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#6d28d9",
    },
  },
};

// Same frame as the app's other confirmation modals.
const bulkApplyModalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "min(92vw, 480px)",
  maxHeight: 800,
  bgcolor: "background.paper",
  border: "2px solid #E2E8F0",
  borderRadius: "15px",
  boxShadow: 24,
  p: 4,
  overflowY: "auto",
};

const numberFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
});

function formatCurrency(value) {
  if (value === null || value === undefined || value === "") {
    return "—";
  }
  const numericValue = Number(value);
  if (Number.isNaN(numericValue)) {
    return value;
  }
  return numberFormatter.format(numericValue);
}

function formatPayPeriodRange(beginning, ending) {
  if (!beginning) {
    return "—";
  }
  const startLabel = formatDateReadableWithDayOfWeek(beginning);
  const endLabel = ending
    ? formatDateReadableWithDayOfWeek(ending)
    : "";
  return endLabel ? `${startLabel} → ${endLabel}` : startLabel;
}

function getCompensationSummary(wage) {
  if (!wage) {
    return "No wage records have been added yet.";
  }

  if (wage.compensation_type === "hourly") {
    return `${formatCurrency(wage.hourly_rate)} per hour`;
  }

  const intervalLabel = salaryIntervalLabels[wage.salary_interval] || "per interval";
  return `${formatCurrency(wage.salary_amount)} ${intervalLabel}`;
}

function formatUpdatedAt(timestamp) {
  if (!timestamp) {
    return "—";
  }
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) {
    return timestamp;
  }
  return date.toLocaleString();
}

function parseLocalDate(dateString) {
  if (!dateString) {
    return null;
  }
  const date = new Date(`${dateString}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date;
}

export function getWageStartDate(wage) {
  return parseLocalDate(
    wage?.pay_period_effective_beginning_date || wage?.effective_date,
  );
}

function getWageScopeKey(wage) {
  return wage?.store_team_member_role_id
    ? `role:${wage.store_team_member_role_id}`
    : "all-roles";
}

function isArchivedRoleWage(wage) {
  return Boolean(
    wage?.store_team_member_role_id && wage?.store_team_member_role_is_archived
  );
}

function compareWageEffectiveOrder(left, right) {
  const leftStartDate = getWageStartDate(left);
  const rightStartDate = getWageStartDate(right);
  const leftEffectiveDate = parseLocalDate(left?.effective_date);
  const rightEffectiveDate = parseLocalDate(right?.effective_date);
  const leftCreatedAt = left?.created_at ? new Date(left.created_at) : null;
  const rightCreatedAt = right?.created_at ? new Date(right.created_at) : null;

  const comparisons = [
    [leftStartDate, rightStartDate],
    [leftEffectiveDate, rightEffectiveDate],
    [leftCreatedAt, rightCreatedAt],
  ];

  for (const [leftDate, rightDate] of comparisons) {
    const leftTime = leftDate && !Number.isNaN(leftDate.getTime())
      ? leftDate.getTime()
      : null;
    const rightTime = rightDate && !Number.isNaN(rightDate.getTime())
      ? rightDate.getTime()
      : null;

    if (leftTime === rightTime) {
      continue;
    }
    if (leftTime === null) {
      return -1;
    }
    if (rightTime === null) {
      return 1;
    }
    return leftTime - rightTime;
  }

  return 0;
}

export function getSupersedingWage(wage, wageHistory = []) {
  return wageHistory
    .filter((candidate) => (
      candidate?.uuid !== wage?.uuid
      && getWageScopeKey(candidate) === getWageScopeKey(wage)
      && compareWageEffectiveOrder(candidate, wage) > 0
    ))
    .sort(compareWageEffectiveOrder)[0] || null;
}

export function getWageStatus(wage, wageHistory = [], todayInput = new Date()) {
  if (isArchivedRoleWage(wage)) {
    return { label: "Historical", color: "text-gray-600", bgColor: "bg-gray-100" };
  }

  const startDate = getWageStartDate(wage);
  if (!startDate) {
    return { label: "Unknown", color: "text-gray-600", bgColor: "bg-gray-100" };
  }

  const today = todayInput instanceof Date
    ? new Date(todayInput)
    : parseLocalDate(todayInput);
  if (!today || Number.isNaN(today.getTime())) {
    return { label: "Unknown", color: "text-gray-600", bgColor: "bg-gray-100" };
  }
  today.setHours(0, 0, 0, 0);

  // If today is before the start date, it's upcoming
  if (today < startDate) {
    return { label: "Upcoming", color: "text-blue-700", bgColor: "bg-blue-100" };
  }

  const supersedingWage = getSupersedingWage(wage, wageHistory);
  const supersedingStartDate = getWageStartDate(supersedingWage);
  if (supersedingStartDate && today >= supersedingStartDate) {
    return { label: "Historical", color: "text-gray-600", bgColor: "bg-gray-100" };
  }

  return { label: "Active", color: "text-green-700", bgColor: "bg-green-100" };
}

export function getCurrentCompensationWages(wageData) {
  const wageHistory = wageData?.wage_history || [];
  const activeWages = wageHistory.filter((wage) => {
    const status = getWageStatus(wage, wageHistory);
    return status.label === "Active";
  });

  if (activeWages.length > 0) {
    return activeWages;
  }

  return wageData?.current_wage && !isArchivedRoleWage(wageData.current_wage)
    ? [wageData.current_wage]
    : [];
}

export default function EmployeePaySection({
  selectedTeamMemberData,
  homeStore,
  fetchStoreTeamMember,
  relatedTeamMembersData,
}) {
  const [wageData, setWageData] = React.useState(null);
  // Start in the loading state so the spinner shows immediately on mount,
  // rather than flashing the empty layout before the fetch effect runs.
  const [loading, setLoading] = React.useState(
    Boolean(selectedTeamMemberData?.id)
  );
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [payPeriods, setPayPeriods] = React.useState([]);
  const [availableRoles, setAvailableRoles] = React.useState([]);
  const [formState, setFormState] = React.useState(INITIAL_FORM_STATE);
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [bulkApplyModalOpen, setBulkApplyModalOpen] = React.useState(false);
  const [pendingPayload, setPendingPayload] = React.useState(null);
  const [historyOpen, setHistoryOpen] = React.useState(false);

  const employeeId = selectedTeamMemberData?.id;
  // The member's exempt status (mirrors user_info.labor_exempt), the drawer's
  // default classification.
  const currentExempt = wageData?.labor_exempt ?? isSalaryType(wageData?.wage_type);

  const handleDrawerOpen = () => setDrawerOpen(true);
  const handleDrawerClose = () => {
    setBulkApplyModalOpen(false);
    setPendingPayload(null);
    setDrawerOpen(false);
    resetForm();
  };

  const computeDefaultEffectiveDate = React.useCallback(() => {
    const todayIso = format(new Date(), "yyyy-MM-dd");

    if (!payPeriods?.length) {
      return todayIso;
    }

    const sorted = [...payPeriods].sort((a, b) => {
      const left = new Date(`${a.beginning_date}T00:00:00`);
      const right = new Date(`${b.beginning_date}T00:00:00`);
      return left.getTime() - right.getTime();
    });

    const today = new Date();
    const upcoming = sorted.find((period) => {
      if (!period?.beginning_date) {
        return false;
      }
      const periodDate = new Date(`${period.beginning_date}T00:00:00`);
      if (Number.isNaN(periodDate.getTime())) {
        return false;
      }
      return periodDate >= today;
    });

    const chosen = upcoming || sorted[sorted.length - 1];
    return chosen?.beginning_date || todayIso;
  }, [payPeriods]);

  const effectiveDateValue = React.useMemo(() => {
    if (!formState.effectiveDate) {
      return null;
    }
    const parsed = new Date(`${formState.effectiveDate}T00:00:00`);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }, [formState.effectiveDate]);

  const roleSelectDisabled = availableRoles.length === 0;
  const otherLocationMemberships = React.useMemo(
    () =>
      (relatedTeamMembersData || []).filter(
        (member) => member?.id && member.id !== employeeId && member?.store?.id,
      ),
    [relatedTeamMembersData, employeeId],
  );
  const otherLocationNames = React.useMemo(
    () =>
      otherLocationMemberships
        .map((member) => member?.store?.store_name)
        .filter(Boolean),
    [otherLocationMemberships],
  );

  React.useEffect(() => {
    setFormState(INITIAL_FORM_STATE);
    setWageData(null);
    setError(null);
    setAvailableRoles([]);
    setBulkApplyModalOpen(false);
    // A save still waiting on the other-locations prompt is cancelled, so
    // the form that started it isn't left saving.
    setPendingPayload((pending) => {
      pending?.resolve(false);
      return null;
    });
  }, [employeeId]);

  // `quiet` refreshes after a save without swapping the form for a skeleton.
  const fetchWageData = React.useCallback(async ({ quiet = false } = {}) => {
    if (!employeeId) {
      return;
    }
    if (!quiet) setLoading(true);
    setError(null);
    try {
      const res = await getRightworkAPI(API_get_store_team_member_wages, {
        store_team_member_id: employeeId,
      });
      const payload = res?.data?.redux?.payload;
      setWageData(payload || null);
      setAvailableRoles(payload?.available_roles || []);
    } catch (err) {
      // A failed background refresh keeps the pay already shown.
      if (quiet) return;
      const message =
        err?.response?.data?.system?.message || "Unable to load wage history.";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  const fetchPayPeriods = React.useCallback(async () => {
    if (!homeStore) {
      return;
    }
    try {
      const res = await getRightworkAPI(API_get_pay_periods, {
        store_id: homeStore,
      });
      const periods = res?.data?.redux?.payload?.pay_periods || [];
      setPayPeriods(periods);
    } catch (err) {
      // Swallow error here, component will show validation when saving.
      console.error("Failed to load pay periods", err);
    }
  }, [homeStore]);

  React.useEffect(() => {
    fetchPayPeriods();
  }, [fetchPayPeriods]);

  React.useEffect(() => {
    fetchWageData();
  }, [fetchWageData]);

  React.useEffect(() => {
    setFormState((prev) => {
      if (prev.wageUuid || prev.effectiveDate) {
        return prev;
      }
      const defaultDate = computeDefaultEffectiveDate();
      if (!defaultDate) {
        return prev;
      }
      return {
        ...prev,
        effectiveDate: defaultDate,
      };
    });
  }, [computeDefaultEffectiveDate]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormState((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleCompensationTypeChange = (event) => {
    const classification = event.target.value;
    const value = classification.startsWith("hourly") ? "hourly" : "salary";
    setFormState((prev) => ({
      ...prev,
      compensationType: value,
      exempt: classification === "salary_exempt" || classification === "hourly_exempt",
      hourlyRate: value === "hourly" ? prev.hourlyRate : "",
      salaryAmount: value === "salary" ? prev.salaryAmount : "",
      salaryInterval: value === "salary" ? prev.salaryInterval || "annual" : "",
    }));
  };

  const handleEffectiveDateChange = (newValue) => {
    if (!newValue || !isValid(newValue)) {
      setFormState((prev) => ({
        ...prev,
        effectiveDate: "",
      }));
      return;
    }
    setFormState((prev) => ({
      ...prev,
      effectiveDate: format(newValue, "yyyy-MM-dd"),
    }));
  };

  const handleRoleSelect = (event) => {
    const value = event.target.value;
    setFormState((prev) => ({
      ...prev,
      storeTeamMemberRoleId: value,
    }));
  };

  const handleSalaryIntervalSelect = (event) => {
    const value = event.target.value;
    setFormState((prev) => ({
      ...prev,
      salaryInterval: value,
    }));
  };

  const resetForm = React.useCallback(() => {
    setFormState({
      ...INITIAL_FORM_STATE,
      effectiveDate: computeDefaultEffectiveDate() || "",
    });
  }, [computeDefaultEffectiveDate]);

  const handleEdit = (wage) => {
    setFormState({
      wageUuid: wage.uuid,
      effectiveDate: wage.effective_date || "",
      storeTeamMemberRoleId: wage.store_team_member_role_id
        ? String(wage.store_team_member_role_id)
        : "",
      compensationType: wage.compensation_type,
      exempt: wage.pending_exempt_status
        ? wage.pending_exempt_status === "exempt"
        : currentExempt,
      hourlyRate: wage.hourly_rate || "",
      salaryAmount: wage.salary_amount || "",
      salaryInterval: wage.salary_interval || "annual",
      notes: wage.notes || "",
    });
    handleDrawerOpen();
  };

  // Returns whether the wage saved. `fromDrawer` closes the drawer and
  // toasts; the inline form shows its own "saved" message.
  const saveWage = React.useCallback(
    async (payload, applyToAllLocations, { fromDrawer = false } = {}) => {
      const requestPayload = {
        ...payload,
        apply_to_all_locations: applyToAllLocations,
      };

      setSaving(true);
      try {
        const res = await postRightworkAPI(
          API_post_store_team_member_wage,
          requestPayload,
        );
        const responsePayload = res?.data?.redux?.payload;
        if (responsePayload) {
          setWageData(responsePayload);
          const bulkApplySummary = responsePayload?.bulk_apply_summary;
          if (applyToAllLocations && bulkApplySummary) {
            const appliedCount = bulkApplySummary?.applied_locations?.length || 0;
            const skippedLocations = bulkApplySummary?.skipped_locations || [];
            const skippedCount = skippedLocations.length;
            let successMessage = "Wage saved successfully.";
            if (appliedCount > 0) {
              successMessage = `Wage saved and applied to ${appliedCount} other location${
                appliedCount === 1 ? "" : "s"
              }.`;
            }
            if (skippedCount > 0) {
              successMessage += ` Skipped ${skippedCount}.`;
            }
            toast.success(successMessage);
            if (skippedCount > 0) {
              const skippedSummary = skippedLocations
                .map((location) => `${location.store_name}: ${location.reason}`)
                .join(" | ");
              toast.info(skippedSummary, { autoClose: 7000 });
            }
          } else if (fromDrawer) {
            toast.success("Wage saved successfully.");
          }
          if (fromDrawer) handleDrawerClose();
          if (typeof fetchStoreTeamMember === "function") {
            fetchStoreTeamMember(false, employeeId);
          }
          return true;
        }
        return false;
      } catch (err) {
        const message =
          err?.response?.data?.system?.message || "Unable to save wage details.";
        if (fromDrawer) {
          toast.error(message);
          return false;
        }
        throw err;
      } finally {
        setSaving(false);
      }
    },
    [employeeId, fetchStoreTeamMember],
  );

  // Save a wage, first asking whether to apply it at the member's other
  // locations. Resolves to whether it saved (false if the prompt is cancelled).
  const requestWageSave = (payload, options = {}) => {
    if (otherLocationMemberships.length === 0) {
      return saveWage(payload, false, options);
    }
    return new Promise((resolve, reject) => {
      setPendingPayload({ payload, options, resolve, reject });
      setBulkApplyModalOpen(true);
    });
  };

  const handleBulkApplyDecision = async (applyToAllLocations) => {
    const pending = pendingPayload;
    setBulkApplyModalOpen(false);
    setPendingPayload(null);
    if (!pending) {
      return;
    }
    try {
      pending.resolve(await saveWage(pending.payload, applyToAllLocations, pending.options));
    } catch (err) {
      pending.reject(err);
    }
  };

  const cancelBulkApply = () => {
    pendingPayload?.resolve(false);
    setBulkApplyModalOpen(false);
    setPendingPayload(null);
  };

  const handleSubmit = async (event) => {
    event?.preventDefault();
    if (!employeeId) {
      toast.error("Employee information is missing.");
      return;
    }

    if (!formState.effectiveDate && !formState.wageUuid) {
      toast.error("Select an effective date for the wage change.");
      return;
    }

    if (formState.compensationType === "hourly" && !formState.hourlyRate) {
      toast.error("Enter an hourly rate.");
      return;
    }

    if (formState.compensationType === "salary") {
      if (!formState.salaryAmount) {
        toast.error("Enter a salary amount.");
        return;
      }
      if (!formState.salaryInterval) {
        toast.error("Select a salary interval.");
        return;
      }
    }

    const payload = {
      store_team_member_id: employeeId,
      effective_date: formState.effectiveDate,
      store_team_member_role_id: formState.storeTeamMemberRoleId || null,
      compensation_type: formState.compensationType,
      hourly_rate:
        formState.compensationType === "hourly" ? formState.hourlyRate : null,
      salary_amount:
        formState.compensationType === "salary" ? formState.salaryAmount : null,
      salary_interval:
        formState.compensationType === "salary"
          ? formState.salaryInterval
          : null,
      notes: formState.notes || null,
      wage_uuid: formState.wageUuid,
      // Applied by the backend when this wage takes effect, at every location
      // it's saved to.
      // Classification follows all-roles pay only.
      ...(formState.storeTeamMemberRoleId
        ? {}
        : { pending_exempt_status: pendingExemptStatusOf(formState) }),
    };

    await requestWageSave(payload, { fromDrawer: true });
  };

  // Pay is entered in Rightwork only when the store manages wages here;
  // otherwise it comes from the POS and is shown read-only.
  const editable = wageData?.pay_source === "rightwork";
  const payType = payTypeOf(wageData);

  const currentCompensationWages = React.useMemo(() => {
    const current = getCurrentCompensationWages(wageData);
    // The POS sends no salary rows, so an hourly row left from before a move
    // to salary is history, not current pay.
    return !editable && payType === "salary"
      ? current.filter((wage) => wage.compensation_type === "salary")
      : current;
  }, [wageData, editable, payType]);

  // Read-only pay line for POS pay; Rightwork pay is edited in the form.
  const sourceLabel = SOURCE_LABELS[wageData?.pay_source] || "your POS";
  const readOnlyPay = currentCompensationWages.length > 0
    ? `${currentCompensationWages
        .map((wage) => (wage.store_team_member_role_name
          ? `${wage.store_team_member_role_name}: ${getCompensationSummary(wage)}`
          : getCompensationSummary(wage)))
        .join(", ")} (from ${sourceLabel})`
    : `${payType === "salary" ? "Salary amount" : "Hourly rate"} isn't available from ${sourceLabel}.`;

  // A saved all-roles change that hasn't started yet.
  const upcomingPay = React.useMemo(() => {
    const history = wageData?.wage_history || [];
    // The next pay period to start; within it, the wage the backend picks
    // (latest effective date, then latest created), as get_wage_for_pay_period.
    const startOf = (wage) => String(wage.pay_period_effective_beginning_date || wage.effective_date);
    const upcoming = history
      .filter((wage) => !wage.store_team_member_role_id && getWageStatus(wage, history).label === "Upcoming")
      .sort((left, right) => (
        startOf(left).localeCompare(startOf(right))
        || String(right.effective_date).localeCompare(String(left.effective_date))
        || String(right.created_at).localeCompare(String(left.created_at))
      ))[0];
    if (!upcoming) return null;
    const start = upcoming.pay_period_effective_beginning_date || upcoming.effective_date;
    return `Changes to ${getCompensationSummary(upcoming)} on ${formatDateReadableWithDayOfWeek(start)}.`;
  }, [wageData]);

  const openRolePayDrawer = () => {
    resetForm();
    setFormState((prev) => ({ ...prev, storeTeamMemberRoleId: String(availableRoles[0]?.id || "") }));
    handleDrawerOpen();
  };

  const refreshAfterSave = ({ laborExempt } = {}) => {
    // An exempt-only save is applied locally right away; the refresh confirms it.
    if (laborExempt !== undefined) {
      setWageData((current) => (current ? { ...current, labor_exempt: laborExempt } : current));
    }
    fetchWageData({ quiet: true });
    if (typeof fetchStoreTeamMember === "function") {
      fetchStoreTeamMember(false, employeeId);
    }
  };

  return (
    <div>
      <EmployeeProfileSectionCard header="Pay" bodyClassName="p-0">
        {loading && !wageData ? (
          <div className="space-y-5 px-6 py-5" aria-busy="true" aria-label="Loading pay">
            {[0, 1].map((row) => (
              <div key={row} className="space-y-2">
                <div className="h-4 w-32 animate-pulse rounded bg-slate-200" />
                <div className="h-10 max-w-sm animate-pulse rounded-lg bg-slate-100" />
              </div>
            ))}
          </div>
        ) : error ? (
          <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-5">
            <p role="alert" className="text-sm text-rose-700">{error}</p>
            <button type="button" onClick={() => fetchWageData()}
              className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50">
              Retry
            </button>
          </div>
        ) : (
          <>
            <EmployeePayForm
              wageData={wageData}
              memberId={employeeId}
              homeStore={homeStore}
              defaultEffectiveDate={computeDefaultEffectiveDate()}
              readOnlyPay={readOnlyPay}
              upcomingPay={upcomingPay}
              onSaveWage={(payload) => requestWageSave(payload)}
              onSaved={refreshAfterSave}
            />
            <div className="flex flex-wrap items-center gap-4 border-t border-slate-200 bg-slate-50 px-6 py-3">
              <button type="button" onClick={() => setHistoryOpen((open) => !open)} aria-expanded={historyOpen}
                className="text-sm font-medium text-violet-700 hover:text-violet-800">
                {historyOpen ? "Hide pay changes" : "View pay changes"}
              </button>
              {editable && availableRoles.length > 0 && (
                <button type="button" onClick={openRolePayDrawer}
                  className="text-sm font-medium text-violet-700 hover:text-violet-800">
                  Add role-specific pay
                </button>
              )}
            </div>
            {!historyOpen ? null : wageData?.wage_history?.length ? (
              <div className="overflow-x-auto border-t border-slate-200">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Period
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Role
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Compensation
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Notes
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Last updated
                      </th>
                      <th className="px-4 py-2" />
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {wageData.wage_history.map((wage) => {
                      const isEditing = formState.wageUuid === wage.uuid;
                      const status = getWageStatus(wage, wageData.wage_history);
                      const supersedingWage = getSupersedingWage(wage, wageData.wage_history);

                      let periodText = "—";
                      if (wage.pay_period_effective_beginning_date) {
                        const startDate = formatDateReadableWithDayOfWeek(wage.pay_period_effective_beginning_date);

                        if (status.label === "Active") {
                          periodText = `Started ${startDate}`;
                        } else if (status.label === "Upcoming") {
                          periodText = `Starting ${startDate}`;
                        } else if (status.label === "Historical") {
                          // For historical wages, show when they ended (when the next wage started)
                          if (supersedingWage?.pay_period_effective_beginning_date) {
                            const endDate = formatDateReadableWithDayOfWeek(supersedingWage.pay_period_effective_beginning_date);
                            periodText = (
                              <>
                                Started {startDate}
                                <div className="text-xs text-gray-500 mt-1">
                                  Ended {endDate}
                                </div>
                              </>
                            );
                          } else {
                            periodText = `Started ${startDate}`;
                          }
                        }
                      }

                      return (
                        <tr key={wage.uuid} className={isEditing ? "bg-violet-50" : ""}>
                          <td className="px-4 py-3 text-sm">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${status.bgColor} ${status.color}`}>
                              {status.label}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {periodText}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {wage.store_team_member_role_name || "All roles"}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {getCompensationSummary(wage)}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            {wage.notes || "—"}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-500">
                            {formatUpdatedAt(wage.updated_at)}
                          </td>
                          <td className="px-4 py-3 text-right">
                            {editable && (
                              <button
                                type="button"
                                onClick={() => handleEdit(wage)}
                                className="text-sm font-medium text-violet-600 hover:text-violet-700"
                              >
                                Edit
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="border-t border-slate-200 px-6 py-4 text-sm text-gray-500">No pay changes yet.</p>
            )}
          </>
        )}
      </EmployeeProfileSectionCard>

      {/* Drawer for Add/Edit Wage */}
      <Drawer open={drawerOpen} anchor="right" onClose={handleDrawerClose}>
        <div className="w-[450px] h-full">
          <div className="max-h-screen overflow-auto">
            <DrawerHeader
              title={formState.wageUuid ? "Edit Wage" : "Add Wage Change"}
              onClose={handleDrawerClose}
            />
            <div className="w-full px-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="mt-5">
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Effective date
                  </label>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DesktopDatePicker
                      value={effectiveDateValue}
                      onChange={handleEffectiveDateChange}
                      format="MM/dd/yyyy"
                      disabled={Boolean(formState.wageUuid)}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: "small",
                          sx: datePickerStyles,
                        },
                        day: {
                          sx: {
                            "&.MuiPickersDay-root.Mui-selected": {
                              backgroundColor: "#6d28d9",
                            },
                          },
                        },
                      }}
                    />
                  </LocalizationProvider>
                  <p className="mt-1 text-xs text-slate-500">
                    The wage begins with the first pay period whose start date is on or after this day.
                  </p>
                  {formState.wageUuid && (
                    <p className="mt-1 text-xs text-slate-500">
                      Effective date cannot be changed while editing an existing wage.
                    </p>
                  )}
                </div>

                <div>
                  <label id="wage-compensation-type-label" htmlFor="wage-compensation-type" className="block text-sm font-medium text-slate-700 mb-1">
                    Pay classification
                  </label>
                  <RightworkSelect
                    id="wage-compensation-type"
                    value={drawerClassificationOf(formState)}
                    onChange={handleCompensationTypeChange}
                    options={drawerClassificationOptions(drawerClassificationOf(formState))}
                    describedBy="wage-compensation-type-help"
                  />
                  <p id="wage-compensation-type-help" className="mt-1 text-xs text-slate-500">
                    {formState.storeTeamMemberRoleId
                      ? "Exempt status follows all-roles pay, so it isn't changed by pay for one role."
                      : "Exempt status changes when this pay takes effect."}
                  </p>
                </div>

                <div>
                  <label id="wage-role-label" htmlFor="wage-role" className="block text-sm font-medium text-slate-700 mb-1">
                    Applies to role
                  </label>
                  <RightworkSelect
                    id="wage-role"
                    value={formState.storeTeamMemberRoleId}
                    onChange={handleRoleSelect}
                    disabled={roleSelectDisabled || Boolean(formState.wageUuid)}
                    options={[
                      { value: "", label: "All roles (default)" },
                      ...availableRoles.map((role) => ({
                        value: String(role.id),
                        label: role.role_type_name
                          ? `${role.role_name} · ${role.role_type_name}`
                          : role.role_name,
                      })),
                    ]}
                    renderValue={(selected) => {
                      if (!selected) {
                        return <span className="text-slate-400">All roles (default)</span>;
                      }
                      const role = availableRoles.find((item) => String(item.id) === String(selected));
                      if (!role) {
                        return "All roles (default)";
                      }
                      return role.role_type_name
                        ? `${role.role_name} · ${role.role_type_name}`
                        : role.role_name;
                    }}
                  />
                  {formState.wageUuid ? (
                    <p className="mt-1 text-xs text-slate-500">
                      A saved wage's role can't be changed. Add a new wage for a different role.
                    </p>
                  ) : roleSelectDisabled && (
                    <p className="mt-1 text-xs text-slate-500">
                      Assign roles to this employee to target a specific role.
                    </p>
                  )}
                </div>

                {formState.compensationType === "hourly" ? (
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">
                      Hourly rate
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      name="hourlyRate"
                      value={formState.hourlyRate}
                      onChange={handleChange}
                      className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-500"
                      placeholder="e.g. 18.50"
                    />
                  </div>
                ) : (
                  <>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">
                        Salary amount
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        name="salaryAmount"
                        value={formState.salaryAmount}
                        onChange={handleChange}
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-500"
                        placeholder="e.g. 52000"
                      />
                    </div>
                    <div>
                      <label id="wage-salary-interval-label" htmlFor="wage-salary-interval" className="block text-sm font-medium text-slate-700 mb-1">
                        Salary interval
                      </label>
                      <RightworkSelect
                        id="wage-salary-interval"
                        value={formState.salaryInterval}
                        onChange={handleSalaryIntervalSelect}
                        options={salaryIntervalOptions}
                        renderValue={(selected) =>
                          salaryIntervalOptions.find((option) => option.value === selected)?.label
                          || <span className="text-slate-400">Select interval</span>}
                      />
                    </div>
                  </>
                )}

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">
                    Notes (optional)
                  </label>
                  <textarea
                    name="notes"
                    value={formState.notes}
                    onChange={handleChange}
                    rows={3}
                    className="w-full border border-slate-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-violet-500"
                    placeholder="Add context about this wage change"
                  />
                </div>
              </form>
            </div>
            <div className="h-[400px]"></div>
          </div>
          <div>
            <DrawerFooterContainerLarge>
              <div className="w-full">
                <LoadingButton
                  variant="contained"
                  loading={saving}
                  loadingPosition="end"
                  endIcon={<SaveIcon />}
                  onClick={handleSubmit}
                  sx={{
                    width: "100%",
                    textTransform: "none",
                    borderRadius: "12px",
                    paddingY: "14px",
                    backgroundColor: "#6d28d9",
                    "&:hover": {
                      backgroundColor: "#5b21b6",
                    },
                    fontSize: "16px",
                  }}
                >
                  {formState.wageUuid ? "Update Wage" : "Save Wage"}
                </LoadingButton>
              </div>
              <div className="w-full mt-3">
                <ButtonOutlined text="Cancel" onClick={handleDrawerClose} />
              </div>
            </DrawerFooterContainerLarge>
          </div>
        </div>
      </Drawer>

      <Modal
        open={bulkApplyModalOpen}
        onClose={() => {
          if (!saving) cancelBulkApply();
        }}
        aria-labelledby="bulk-wage-apply-title"
        aria-describedby="bulk-wage-apply-description"
      >
        <Box sx={bulkApplyModalStyle} role="dialog" aria-modal="true"
          aria-labelledby="bulk-wage-apply-title" aria-describedby="bulk-wage-apply-description">
          <button
            type="button"
            aria-label="Cancel"
            className="absolute right-2 top-2 text-gray-500 hover:text-gray-700 disabled:cursor-not-allowed"
            onClick={cancelBulkApply}
            disabled={saving}
          >
            <CloseIcon />
          </button>
          <div className="flex items-center space-x-2">
            <div className="text-violet-700">
              <StorefrontIcon style={{ marginBottom: "1px" }} />
            </div>
            <h3 id="bulk-wage-apply-title" className="text-2xl text-slate-900">
              Apply to other locations?
            </h3>
          </div>
          <p id="bulk-wage-apply-description" className="text-slate-500 text-sm mt-4">
            {selectedTeamMemberData?.first_name || "This employee"} also works at{" "}
            <span className="font-semibold text-slate-700">{otherLocationNames.join(", ") || "other locations"}</span>.
            {" "}Apply this pay change there too, or only at this location?
          </p>
          {pendingPayload?.payload?.store_team_member_role_id && (
            <p className="text-amber-700 text-sm mt-3">
              Role-specific pay only applies where the employee already has the matching role. Other locations are skipped.
            </p>
          )}
          <div className="w-full flex justify-end mt-8">
            <div className="flex flex-wrap justify-end gap-3">
              <button
                type="button"
                className="px-8 py-2 bg-violet-700 hover:bg-violet-900 text-white rounded-lg text-base disabled:cursor-not-allowed disabled:opacity-60"
                onClick={() => handleBulkApplyDecision(true)}
                disabled={saving}
              >
                {saving ? "Saving…" : "Apply to all locations"}
              </button>
              <button
                type="button"
                className="px-8 py-2 bg-white border border-violet-700 text-violet-700 hover:bg-slate-100 rounded-lg text-base disabled:cursor-not-allowed disabled:opacity-60"
                onClick={() => handleBulkApplyDecision(false)}
                disabled={saving}
              >
                This location only
              </button>
            </div>
          </div>
        </Box>
      </Modal>
    </div>
  );
}
